/**
 * Thème clair / sombre / système.
 *
 * Règle de la charte (§6) : le défaut **suit le système**, et seul un choix **explicite**
 * de la personne est persisté.
 *
 * 🪤 La nuance n'est pas cosmétique. Une première version persistait le thème à chaque
 * application — donc dès le premier affichage. Le thème système se trouvait figé au hasard
 * de la première visite : qui bascule son Mac en clair à midi gardait une application
 * sombre, sans avoir jamais rien choisi.
 *
 * D'où les **trois modes** : « système » est un choix comme les autres, et c'est le seul
 * moyen de revenir en arrière après avoir choisi.
 */
const KEY = 'mnemo.theme';

export type Theme = 'light' | 'dark';
export type ThemeMode = 'system' | Theme;

function systemTheme(): Theme {
  return matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/** Le mode retenu : ce que la personne a choisi, ou « système » si elle n'a rien choisi. */
export function currentMode(): ThemeMode {
  try {
    const saved = localStorage.getItem(KEY);
    if (saved === 'light' || saved === 'dark') return saved;
  } catch {
    /* stockage indisponible (navigation privée) : on suit le système */
  }
  return 'system';
}

/** Le thème effectif qui découle d'un mode. */
export function themeFor(mode: ThemeMode): Theme {
  return mode === 'system' ? systemTheme() : mode;
}

/** Applique au document. N'écrit rien : appelé à chaque rendu, y compris sans choix. */
export function applyTheme(theme: Theme): void {
  document.documentElement.classList.toggle('dark', theme === 'dark');
}

/** Enregistre un choix EXPLICITE. « système » efface le choix au lieu d'en poser un. */
export function persistMode(mode: ThemeMode): void {
  try {
    if (mode === 'system') localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, mode);
  } catch {
    /* sans effet : le thème vaudra pour cette session seulement */
  }
}

/**
 * S'abonne aux changements du système, tant que la personne n'a rien choisi.
 * Renvoie la fonction de désabonnement.
 */
export function watchSystemTheme(onChange: (theme: Theme) => void): () => void {
  const media = matchMedia('(prefers-color-scheme: dark)');
  const handler = () => {
    if (currentMode() === 'system') onChange(media.matches ? 'dark' : 'light');
  };
  media.addEventListener('change', handler);
  return () => media.removeEventListener('change', handler);
}
